import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { type CongaTestRow, useGetCongaTestRowWebhookLogsQuery } from '@/services/congaTestApi';
import { PalmettoButton, PalmettoModal, PalmettoTag } from '@palmetto/dsm-components';
import { Column, Table } from '@palmetto/palmetto-components';
import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { CongaMergeDocument, CongaTestEnvelopeStatus } from 'types';

import { useCongaBatchStream } from './hooks/useCongaBatchStream';

const statusTagColor = {
  [CongaTestEnvelopeStatus.pending]: 'grey',
  [CongaTestEnvelopeStatus.submitting]: 'volt',
  [CongaTestEnvelopeStatus.awaitingSigningUrls]: 'volt',
  [CongaTestEnvelopeStatus.succeeded]: 'success',
  [CongaTestEnvelopeStatus.failed]: 'error',
} as const satisfies Record<CongaTestEnvelopeStatus, 'grey' | 'volt' | 'success' | 'error'>;

const statusLabel: Record<CongaTestEnvelopeStatus, string> = {
  [CongaTestEnvelopeStatus.pending]: 'pending',
  [CongaTestEnvelopeStatus.submitting]: 'submitting',
  [CongaTestEnvelopeStatus.awaitingSigningUrls]: 'awaiting signing URLs',
  [CongaTestEnvelopeStatus.succeeded]: 'succeeded',
  [CongaTestEnvelopeStatus.failed]: 'failed',
};

const isComplete = (row: CongaTestRow): boolean =>
  row.status === CongaTestEnvelopeStatus.succeeded || row.status === CongaTestEnvelopeStatus.failed;

// Rows that failed validation have no payload, and batches saved before the v6 payload have no Signers, so
// fall back to whatever emails the CSV row had.
const getSignerEmails = (row: CongaTestRow): string[] =>
  row.payload?.Signers?.map((signer) => signer.Email) ??
  Object.entries(row.csvRow)
    .filter(([column, value]) => column.endsWith('.Email') && value)
    .map(([, value]) => value);

// A webhook can land before the generate response records the ids, so documents known only from webhooks are kept.
const getMergeDocumentIds = (row: CongaTestRow): string[] => [
  ...new Set([...(row.result?.mergeDocumentIds ?? []), ...Object.keys(row.mergeDocuments ?? {})]),
];

// Conga's document URLs need its bearer token, so the links go through our API, which authenticates with the session cookie.
const buildMergeDocumentUrl = (
  batchId: string,
  rowId: string,
  documentId: string,
  kind: 'download' | 'preview',
): string =>
  `/api/internal/conga-test-envelopes/batches/${batchId}/rows/${rowId}/merge-documents/${encodeURIComponent(documentId)}/${kind}`;

interface MergeDocumentProps {
  batchId: string;
  rowId: string;
  id: string;
  document?: CongaMergeDocument;
}

const MergeDocument = ({ batchId, rowId, id, document }: MergeDocumentProps) => (
  <div className="flex flex-col">
    <span>{id}</span>
    <span className="text-xs text-text-secondary">{document?.status ?? 'awaiting webhook'}</span>
    {document?.downloadUrl && (
      <a
        href={buildMergeDocumentUrl(batchId, rowId, id, 'download')}
        target="_blank"
        rel="noreferrer"
        className="text-text-link text-xs"
      >
        Download
      </a>
    )}
    {document?.previewUrl && (
      <a
        href={buildMergeDocumentUrl(batchId, rowId, id, 'preview')}
        target="_blank"
        rel="noreferrer"
        className="text-text-link text-xs"
      >
        Preview
      </a>
    )}
    {document?.error && <span className="text-xs text-text-error">{document.error}</span>}
  </div>
);

const formatEventTime = (receivedAt: string): string => new Date(receivedAt).toLocaleTimeString();

const hasWebhookLogs = (row: CongaTestRow): boolean => (row.events ?? []).some((event) => event.webhookLogId);

const copyToClipboard = (text: string): void => {
  void navigator.clipboard.writeText(text);
};

const formatLog = (data: unknown): string => JSON.stringify(data, null, 2);

const WebhookLogsModal = ({ batchId, rowId, onClose }: { batchId: string; rowId: string; onClose: () => void }) => {
  // The row list updates live, so a reopened modal must not show a stale cached delivery list.
  const { data, isFetching, error } = useGetCongaTestRowWebhookLogsQuery(
    { batchId, rowId },
    { refetchOnMountOrArgChange: true },
  );
  const logs = data?.logs ?? [];

  return (
    <PalmettoModal
      open
      onClose={onClose}
      invertedBackground={false}
      bottomContent={
        <div className="flex justify-end gap-8">
          <PalmettoButton
            type="button"
            emphasis="secondary"
            layout="hug"
            disabled={logs.length === 0}
            onClick={() => copyToClipboard(formatLog(logs.map((log) => log.data)))}
          >
            Copy all
          </PalmettoButton>
          <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={onClose}>
            Close
          </PalmettoButton>
        </div>
      }
    >
      <div className="flex flex-col gap-16">
        <h2 className="heading-300">Raw webhook logs</h2>
        {isFetching && !data && <span className="text-sm text-text-secondary">Loading…</span>}
        {error && (
          <span role="alert" className="text-sm text-text-error">
            Failed to load webhook logs
          </span>
        )}
        {!isFetching && !error && logs.length === 0 && (
          <span className="text-sm text-text-secondary">No webhook logs found. They expire after 90 days.</span>
        )}
        {logs.map((log) => (
          <div key={log.id} className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-8 text-xs">
              <span>
                <span className="font-bold">{log.inboundEvent ?? log.event}</span>{' '}
                <span className="text-text-secondary">
                  {log.event}
                  {log.receivedAt ? ` · ${new Date(log.receivedAt).toLocaleTimeString()}` : ''}
                </span>
              </span>
              <PalmettoButton
                type="button"
                emphasis="secondary"
                layout="hug"
                onClick={() => copyToClipboard(formatLog(log.data))}
              >
                Copy
              </PalmettoButton>
            </div>
            <pre className="rounded bg-background-secondary max-h-[320px] overflow-auto p-8 font-mono text-xs">
              {formatLog(log.data)}
            </pre>
          </div>
        ))}
      </div>
    </PalmettoModal>
  );
};

const buildColumns = (batchId: string, onViewLogs: (rowId: string) => void): Column[] => [
  { heading: '#', dataKey: 'rowNumber', width: 60 },
  { heading: 'Label', dataKey: 'label', render: (_cell, row) => (row as CongaTestRow).label ?? '' },
  {
    heading: 'Signers',
    dataKey: 'signers',
    render: (_cell, row) => (
      <div className="flex flex-col gap-2">
        {getSignerEmails(row as CongaTestRow).map((email) => (
          <span key={email}>{email}</span>
        ))}
      </div>
    ),
  },
  {
    heading: 'Status',
    dataKey: 'status',
    width: 130,
    render: (_cell, row) => {
      const { status } = row as CongaTestRow;
      return <PalmettoTag color={statusTagColor[status]} label={statusLabel[status]} size="sm" />;
    },
  },
  {
    heading: 'Merge Request ID',
    dataKey: 'mergeRequestId',
    render: (_cell, row) => (row as CongaTestRow).result?.mergeRequestId ?? '',
  },
  {
    heading: 'Merge Documents',
    dataKey: 'mergeDocumentIds',
    render: (_cell, row) => {
      const { id: rowId, mergeDocuments } = row as CongaTestRow;
      return (
        <div className="flex flex-col gap-4">
          {getMergeDocumentIds(row as CongaTestRow).map((documentId) => (
            <MergeDocument
              key={documentId}
              batchId={batchId}
              rowId={rowId}
              id={documentId}
              document={mergeDocuments?.[documentId]}
            />
          ))}
        </div>
      );
    },
  },
  {
    heading: 'Webhook Events',
    dataKey: 'events',
    render: (_cell, row) => (
      <div className="flex flex-col gap-4">
        <ol className="flex flex-col gap-2">
          {((row as CongaTestRow).events ?? []).map((event) => (
            // receivedAt is our own millisecond timestamp, so even a redelivered event gets a distinct key.
            <li
              key={`${event.receivedAt}-${event.source}-${event.event}-${event.mergeDocumentId}`}
              className="text-xs"
              title={event.error}
            >
              <span className="font-bold">{event.event}</span>{' '}
              <span className="text-text-secondary">{formatEventTime(event.receivedAt)}</span>
            </li>
          ))}
        </ol>
        {hasWebhookLogs(row as CongaTestRow) && (
          <button
            type="button"
            className="text-text-link self-start text-xs"
            onClick={() => onViewLogs((row as CongaTestRow).id)}
          >
            Raw logs
          </button>
        )}
      </div>
    ),
  },
  {
    heading: 'Signing URLs',
    dataKey: 'signingUrls',
    render: (_cell, row) => (
      <div className="flex flex-col gap-2">
        {((row as CongaTestRow).signingUrls ?? []).map((signingUrl) => (
          <a
            key={signingUrl.signer}
            href={signingUrl.url}
            target="_blank"
            rel="noreferrer"
            className="text-text-link"
          >
            {signingUrl.signer}
          </a>
        ))}
      </div>
    ),
  },
  {
    heading: 'Error',
    dataKey: 'error',
    render: (_cell, row) => {
      const { error } = row as CongaTestRow;
      return error ? <span className="text-xs text-text-error">{error}</span> : null;
    },
  },
];

export const CongaTestBatchPage = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const { rows, isFinished, error } = useCongaBatchStream(batchId);

  const [logsRowId, setLogsRowId] = useState<string>();
  const columns = useMemo(() => buildColumns(batchId ?? '', setLogsRowId), [batchId]);
  const closeLogs = useCallback(() => setLogsRowId(undefined), []);

  const completeCount = useMemo(() => rows.filter(isComplete).length, [rows]);

  return (
    <DsmPageContainer title="Conga Test Envelopes">
      <PageHeader title="Conga Test Envelopes">
        <Link to="/internal/conga-test">
          <PalmettoButton type="button" emphasis="secondary">
            New upload
          </PalmettoButton>
        </Link>
      </PageHeader>
      <div className="mx-6 flex flex-col gap-16">
        <span className="text-sm text-text-secondary">
          {completeCount} / {rows.length} complete{isFinished ? '' : ' (processing…)'}
        </span>
        {error && (
          <span role="alert" className="text-sm text-text-error">
            {error}
          </span>
        )}
        <Table isLoading={false} rowKey="id" columns={columns} rows={rows} isScrollable={{ x: true }} />
      </div>
      {logsRowId && batchId && <WebhookLogsModal batchId={batchId} rowId={logsRowId} onClose={closeLogs} />}
    </DsmPageContainer>
  );
};
